import React from "react";
import { ClockIcon } from "@heroicons/react/outline";

const PaymentAgingChart = ({ agingData, loading }) => {
  const agingBuckets = [
    {
      key: "0-24h",
      label: "0-24 hours",
      color: "bg-green-500",
      textColor: "text-green-700",
      bgColor: "bg-green-50",
    },
    {
      key: "1-3d",
      label: "1-3 days",
      color: "bg-yellow-500",
      textColor: "text-yellow-700",
      bgColor: "bg-yellow-50",
    },
    {
      key: "3-7d",
      label: "3-7 days",
      color: "bg-orange-500",
      textColor: "text-orange-700",
      bgColor: "bg-orange-50",
    },
    {
      key: "7d+",
      label: "7+ days",
      color: "bg-red-500",
      textColor: "text-red-700",
      bgColor: "bg-red-50",
    },
  ];

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-8 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const totalPayments = Object.values(agingData).reduce(
    (sum, bucket) => sum + (bucket?.length || 0),
    0
  );

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-medium text-gray-900">
          Payment Verification Aging
        </h2>
        <div className="flex items-center text-sm text-gray-500">
          <ClockIcon className="h-4 w-4 mr-1" />
          <span>{totalPayments} pending</span>
        </div>
      </div>

      {totalPayments === 0 ? (
        <div className="flex items-center justify-center py-8 text-gray-500">
          <div className="text-center">
            <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
            <p className="text-sm">No pending payment verifications</p>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {agingBuckets.map((bucket) => {
            const count = agingData[bucket.key]?.length || 0;
            const percentage =
              totalPayments > 0 ? (count / totalPayments) * 100 : 0;

            return (
              <div key={bucket.key}>
                <div className="flex justify-between text-sm mb-1">
                  <span className={`font-medium ${bucket.textColor}`}>
                    {bucket.label}
                  </span>
                  <span className="text-gray-600">
                    {count} payment{count !== 1 ? "s" : ""} (
                    {percentage.toFixed(0)}%)
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className={`${bucket.color} h-3 rounded-full transition-all duration-300 flex items-center justify-end pr-2`}
                    style={{ width: `${percentage}%` }}
                  >
                    {count > 0 && percentage > 15 && (
                      <span className="text-white text-xs font-medium">
                        {count}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {/* SLA Warning */}
          {agingData["7d+"]?.length > 0 && (
            <div
              className={`mt-4 p-3 rounded-lg bg-red-50 border border-red-200`}
            >
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-red-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    SLA Breach Alert
                  </h3>
                  <div className="mt-1 text-xs text-red-700">
                    {agingData["7d+"].length} payment
                    {agingData["7d+"].length !== 1 ? "s" : ""} pending for more
                    than 7 days. Immediate action required.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* SLA Success */}
          {agingData["7d+"]?.length === 0 && totalPayments > 0 && (
            <div className="mt-4 p-3 rounded-lg bg-green-50 border border-green-200">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-green-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-xs text-green-700">
                    All pending verifications within SLA (7 days)
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PaymentAgingChart;
